// The cross's pose model, shared by the landing page and the studio so both
// place the cross identically. No three.js import: the studio's pure timeline
// logic (and its node tests) build on this module.
//
// A pose is [rotY, rotX, rotZ, posX, posY, camZ]: radians, world units, and
// the camera's distance. Angles are never wrapped, so 2π means "one full turn".

export const POSE_FIELDS = ["rotY", "rotX", "rotZ", "posX", "posY", "camZ"];

// keyframes over scroll progress 0..1
// one continuous motion: sideways logo close-up swings upright while the
// camera pulls back, completing a single full turn into the buy position
export const SITE_KEYFRAMES = [
  { p: 0.00, v: [-1.5708, 0.0, -1.5708,  0.85,  0.0, 3.0] },
  { p: 0.35, v: [ 0.60,   0.0,  0.0,     0.0,  -0.1, 7.8] },
  { p: 1.00, v: [ 6.283,  0.0,  0.0,    -1.25, -0.1, 8.4] },
];

// Phones (PHONE_QUERY, the page's phone breakpoint): the same motion, but
// the buy position is the whole cross centred in the narrow frame, above the
// product copy and bottom bars, instead of left of centre beside them.
export const PHONE_QUERY = "(max-width: 749px)";
export const SITE_KEYFRAMES_PHONE = [
  SITE_KEYFRAMES[0],
  SITE_KEYFRAMES[1],
  { p: 1.00, v: [ 6.283,  0.0,  0.0,     0.0,   0.75, 11.5] },
];

// The redesign (?page=redesign) on a wide screen: the same swoop out of the
// sideways close-up, but the cross arrives left of centre as its text comes
// in beside it on the right (reveal.js's COVER_TEXT, 0.40 to 0.54), and
// turns there through the text's beat, by scroll, to face the visitor as
// the text goes and it scrolls away (COVER_CROSS). The page's own end pose
// (left at 1.00, the turntable from 0.92) is past the redesign's text: there
// the cross would still be centred and edge-on, half way round.
export const SITE_KEYFRAMES_REDESIGN_WIDE = [
  SITE_KEYFRAMES[0],
  { p: 0.35, v: [ 0.60,   0.0,  0.0,    -0.6,  -0.1, 7.8] },
  { p: 0.40, v: [ 1.05,   0.0,  0.0,    -1.0,  -0.1, 8.4] },
  { p: 0.54, v: [ 6.283,  0.0,  0.0,    -1.0,  -0.1, 8.4] },
  { p: 1.00, v: [ 6.283,  0.0,  0.0,    -1.0,  -0.1, 8.4] },
];

// The redesign (?page=redesign) under its own breakpoint (1000 px wide,
// js/redesign/desk.js's WIDE_QUERY): the phone's motion, the cross ending
// centred, a little higher, so the product's text stands under it over the
// stage, above the pill (css/redesign/cover.css).
export const REDESIGN_NARROW_QUERY = "(max-width: 999px)";
export const SITE_KEYFRAMES_REDESIGN_NARROW = [
  SITE_KEYFRAMES[0],
  SITE_KEYFRAMES[1],
  { p: 1.00, v: [ 6.283,  0.0,  0.0,     0.0,   1.05, 11.5] },
];

// The pose at scroll progress p (0..1) along `keyframes`, linear between
// stops (no per-segment easing, so the motion never stalls).
export function poseAt(keyframes, p) {
  let a = keyframes[0], b = keyframes[keyframes.length - 1];
  for (let i = 0; i < keyframes.length - 1; i++) {
    if (p >= keyframes[i].p && p <= keyframes[i + 1].p) { a = keyframes[i]; b = keyframes[i + 1]; break; }
  }
  const t = (p - a.p) / (b.p - a.p || 1);
  return a.v.map((v, i) => v + (b.v[i] - v) * t);
}

// Places the cross ({ group, camera } from createCrossScene) at `pose`.
export function applyPose({ group, camera }, pose) {
  // "ZXY": spin about the cross's own long axis first, then tilt about the
  // screen's horizontal axis, then roll in the screen plane — so a tilt reads
  // the same on screen whichever side of the cross is facing the camera
  group.rotation.set(pose[1], pose[0], pose[2], "ZXY");
  group.position.x = pose[3];
  group.position.y = pose[4];
  camera.position.z = pose[5];
}
